import type { ActiveTab } from './PaperWriterContext';

export const TABS_CONFIG: { id: ActiveTab; label: string; icon: string }[] = [
  { id: 'upload',      label: 'Upload Files',    icon: 'ri-upload-cloud-line' },
  { id: 'templates',   label: 'Templates',       icon: 'ri-layout-grid-line' },
  { id: 'options',     label: 'Paper Options',   icon: 'ri-settings-3-line' },
  { id: 'aiproof',     label: 'AI-Proof',        icon: 'ri-shield-star-line' },
  { id: 'proofread',   label: 'Proofread',       icon: 'ri-spell-check-line' },
  { id: 'plagiarism',  label: 'Plagiarism',      icon: 'ri-search-eye-line' },
  { id: 'citation',    label: 'Citations',       icon: 'ri-double-quotes-l' },
  { id: 'reference',   label: 'References',      icon: 'ri-edit-box-line' },
  { id: 'readability', label: 'Readability',     icon: 'ri-bar-chart-box-line' },
];

export const ACCEPTED_TYPES = ['PDF', 'DOCX', 'TXT', 'LaTeX', 'MD', 'BIB', 'CSV', 'Images'];

export const QUICK_PROMPTS = [
  'The impact of climate change on global food security',
  'Machine learning applications in healthcare diagnostics',
  "Social media's effect on adolescent mental health",
  'Renewable energy transition in developing economies',
  'Ethical implications of artificial general intelligence',
];

export const PAPER_TYPES = ['Research Paper', 'Essay', 'Thesis', 'Dissertation', 'Literature Review', 'Case Study', 'Report', 'Article'];
export const ACADEMIC_LEVELS = ['High School', 'Undergraduate', 'Graduate', 'PhD', 'Professional'];
export const CITATION_STYLES = ['APA', 'MLA', 'Chicago', 'IEEE', 'Harvard', 'Vancouver'];
export const WORD_COUNTS = ['500–1,000', '1,000–2,500', '2,500–5,000', '5,000–10,000', '10,000+'];
export const LANGUAGES = ['English', 'Spanish', 'French', 'German', 'Portuguese', 'Italian', 'Chinese', 'Japanese'];
export const TONES = ['Academic', 'Formal', 'Analytical', 'Conversational', 'Persuasive', 'Descriptive'];
export const WRITING_STYLES = ['Academic', 'Conversational', 'Formal', 'Analytical', 'Narrative'];

export const GENERATE_STEPS = [
  'Analyzing uploaded research files…',
  'Processing topic description…',
  'Structuring paper outline…',
  'Generating introduction…',
  'Writing body sections…',
  'Compiling references…',
  'Applying citation style…',
  'Running AI-proof humanization…',
  'Finalizing and polishing…',
  '✅ Paper generated successfully!',
];

export interface PaperTemplate {
  id: string;
  title: string;
  category: string;
  categoryColor: string;
  categoryBg: string;
  icon: string;
  description: string;
  topic: string;
  paperType: string;
  academicLevel: string;
  citationStyle: string;
  wordCount: string;
  tone: string;
  tags: string[];
}

export const PAPER_TEMPLATES: PaperTemplate[] = [
  {
    id: 'lit-review-ai',
    title: 'AI in Healthcare — Literature Review',
    category: 'Literature Review',
    categoryColor: 'text-emerald-700',
    categoryBg: 'bg-emerald-50',
    icon: 'ri-book-2-line',
    description: 'A systematic review of AI-driven diagnostic tools, clinical decision support, and patient outcome improvements.',
    topic: 'Artificial intelligence applications in healthcare diagnostics and clinical decision support: a systematic literature review of machine learning models, deep learning algorithms, and their impact on patient outcomes from 2018 to 2024',
    paperType: 'Literature Review',
    academicLevel: 'Graduate',
    citationStyle: 'APA',
    wordCount: '2,500–5,000',
    tone: 'Academic',
    tags: ['AI', 'Healthcare', 'Machine Learning', 'Diagnostics'],
  },
  {
    id: 'case-study-tesla',
    title: 'Tesla\'s Disruptive Innovation — Case Study',
    category: 'Case Study',
    categoryColor: 'text-rose-700',
    categoryBg: 'bg-rose-50',
    icon: 'ri-building-line',
    description: 'Analyzes Tesla\'s business model, EV market disruption, and strategic positioning in the clean energy sector.',
    topic: 'Tesla Inc. as a disruptive innovator: a case study examining electric vehicle market penetration, vertical integration strategy, brand positioning, and the competitive dynamics of the global automotive industry',
    paperType: 'Case Study',
    academicLevel: 'Undergraduate',
    citationStyle: 'Chicago',
    wordCount: '1,000–2,500',
    tone: 'Analytical',
    tags: ['Business', 'Innovation', 'EV', 'Strategy'],
  },
  {
    id: 'research-climate',
    title: 'Climate Change & Food Security',
    category: 'Research Paper',
    categoryColor: 'text-teal-700',
    categoryBg: 'bg-teal-50',
    icon: 'ri-plant-line',
    description: 'Investigates how rising temperatures, extreme weather, and shifting precipitation affect global crop yields.',
    topic: 'The impact of climate change on global food security: examining the effects of rising temperatures, extreme weather events, and shifting precipitation patterns on agricultural productivity, crop yields, and food distribution systems in developing nations',
    paperType: 'Research Paper',
    academicLevel: 'Graduate',
    citationStyle: 'APA',
    wordCount: '2,500–5,000',
    tone: 'Academic',
    tags: ['Climate', 'Agriculture', 'Food Security', 'Environment'],
  },
  {
    id: 'essay-social-media',
    title: 'Social Media & Mental Health Essay',
    category: 'Essay',
    categoryColor: 'text-amber-700',
    categoryBg: 'bg-amber-50',
    icon: 'ri-mental-health-line',
    description: 'Critical essay exploring the psychological effects of social media use on adolescent mental health and self-esteem.',
    topic: 'The psychological effects of social media use on adolescent mental health: a critical analysis of anxiety, depression, cyberbullying, social comparison, and the role of platform design in shaping teenage self-esteem and identity formation',
    paperType: 'Essay',
    academicLevel: 'Undergraduate',
    citationStyle: 'MLA',
    wordCount: '1,000–2,500',
    tone: 'Analytical',
    tags: ['Psychology', 'Social Media', 'Adolescents', 'Mental Health'],
  },
  {
    id: 'thesis-blockchain',
    title: 'Blockchain in Supply Chain — Thesis',
    category: 'Thesis',
    categoryColor: 'text-indigo-700',
    categoryBg: 'bg-indigo-50',
    icon: 'ri-links-line',
    description: 'Examines how distributed ledger technology improves transparency, traceability, and efficiency in global supply chains.',
    topic: 'Blockchain technology as a solution for supply chain transparency and traceability: a theoretical and empirical investigation into distributed ledger applications, smart contracts, and their effectiveness in reducing fraud, improving efficiency, and enhancing stakeholder trust',
    paperType: 'Thesis',
    academicLevel: 'Graduate',
    citationStyle: 'IEEE',
    wordCount: '5,000–10,000',
    tone: 'Formal',
    tags: ['Blockchain', 'Supply Chain', 'Technology', 'Logistics'],
  },
  {
    id: 'report-renewable',
    title: 'Renewable Energy Transition Report',
    category: 'Report',
    categoryColor: 'text-cyan-700',
    categoryBg: 'bg-cyan-50',
    icon: 'ri-sun-line',
    description: 'Policy-focused report on the challenges and opportunities of transitioning to renewable energy in developing economies.',
    topic: 'Renewable energy transition in developing economies: a policy report examining solar, wind, and hydroelectric adoption rates, infrastructure investment requirements, government incentive programs, and barriers to clean energy access in Sub-Saharan Africa and Southeast Asia',
    paperType: 'Report',
    academicLevel: 'Professional',
    citationStyle: 'Harvard',
    wordCount: '2,500–5,000',
    tone: 'Formal',
    tags: ['Energy', 'Policy', 'Sustainability', 'Development'],
  },
  {
    id: 'lit-review-remote-work',
    title: 'Remote Work Productivity — Literature Review',
    category: 'Literature Review',
    categoryColor: 'text-emerald-700',
    categoryBg: 'bg-emerald-50',
    icon: 'ri-computer-line',
    description: 'Reviews empirical studies on how remote work affects employee productivity, collaboration, and organizational culture.',
    topic: 'Remote work and employee productivity: a systematic literature review of empirical studies examining the effects of telecommuting on individual performance, team collaboration, work-life balance, organizational culture, and managerial challenges in the post-pandemic era',
    paperType: 'Literature Review',
    academicLevel: 'Graduate',
    citationStyle: 'APA',
    wordCount: '2,500–5,000',
    tone: 'Academic',
    tags: ['Remote Work', 'Productivity', 'Management', 'HR'],
  },
  {
    id: 'case-study-amazon',
    title: 'Amazon\'s Platform Economy — Case Study',
    category: 'Case Study',
    categoryColor: 'text-rose-700',
    categoryBg: 'bg-rose-50',
    icon: 'ri-store-line',
    description: 'Analyzes Amazon\'s multi-sided platform strategy, AWS dominance, and ecosystem lock-in mechanisms.',
    topic: 'Amazon\'s platform economy strategy: a case study of multi-sided marketplace dynamics, AWS cloud dominance, Prime ecosystem lock-in, third-party seller relationships, and the competitive implications of vertical integration in e-commerce and cloud computing',
    paperType: 'Case Study',
    academicLevel: 'Graduate',
    citationStyle: 'Chicago',
    wordCount: '2,500–5,000',
    tone: 'Analytical',
    tags: ['Amazon', 'Platform', 'E-commerce', 'Strategy'],
  },
  {
    id: 'research-gene-editing',
    title: 'CRISPR Gene Editing Ethics',
    category: 'Research Paper',
    categoryColor: 'text-teal-700',
    categoryBg: 'bg-teal-50',
    icon: 'ri-dna-line',
    description: 'Explores the ethical, legal, and societal implications of CRISPR-Cas9 gene editing in humans.',
    topic: 'Ethical, legal, and societal implications of CRISPR-Cas9 gene editing in human embryos: examining germline modification, designer babies, genetic inequality, regulatory frameworks, and the balance between therapeutic potential and ethical boundaries in biomedical research',
    paperType: 'Research Paper',
    academicLevel: 'PhD',
    citationStyle: 'Vancouver',
    wordCount: '5,000–10,000',
    tone: 'Academic',
    tags: ['CRISPR', 'Bioethics', 'Genetics', 'Medicine'],
  },
];